import { ReactNode, useMemo } from "react";
import { GlobalStyles, ThemeProvider, useTheme } from "@mui/material";
import { createTheme } from "@mui/material/styles";

import { ASK_GENIE_RESIZING_ATTRIBUTE } from "@/common/genie/dock/genieDockLayout";

import { PAGE_RIGHT_INSET_VARIABLE } from "./pageInset";

/**
 * Marks a modal that belongs to Genie itself — its panel and the dialogs it
 * opens — so it keeps its own place instead of moving into the page area.
 */
export const GENIE_SURFACE_CLASS = "admin-genie-surface";

/** Every page modal: dialogs and temporary drawers, from any page or library. */
const PAGE_MODALS = [".MuiDialog-root", ".MuiDrawer-modal"].map(
  (selector) => `${selector}:not(.${GENIE_SURFACE_CLASS})`
);
/**
 * The root and its backdrop: the backdrop is `position: fixed` itself, so it
 * is placed against the viewport, not the root, and is inset on its own.
 */
const PAGE_MODAL_SELECTOR = PAGE_MODALS.flatMap((selector) => [
  selector,
  `${selector} > .MuiBackdrop-root`,
]).join(", ");
/** While Genie is being resized the page modals follow the edge, not trail it. */
const RESIZING_PAGE_MODAL_SELECTOR = PAGE_MODAL_SELECTOR.split(", ")
  .map((selector) => `html[${ASK_GENIE_RESIZING_ATTRIBUTE}] ${selector}`)
  .join(", ");

/**
 * Keeps every page dialog beside a docked Genie instead of over it
 * (GEN5-5185), without touching the dialogs themselves:
 *
 * - The room Genie takes is set on `:root`, where portalled modals can read
 *   it, and every page modal's root and backdrop end where Genie starts. A dialog is centred in the page area, its
 *   backdrop dims only the page, and Genie stays visible and usable.
 * - While Genie is open, a page dialog no longer pulls focus back to itself,
 *   so the admin can type to Genie with a dialog open (Tab can also leave
 *   the dialog for Genie). Escape and backdrop clicks work as before: Genie
 *   is not a modal, so the page dialog stays the top one.
 */
export function PageAreaModals({
  inset,
  isGenieOpen,
  children,
}: {
  /** The CSS width Genie takes on the right; "0px" when it covers nothing. */
  inset: string;
  isGenieOpen: boolean;
  children: ReactNode;
}) {
  const parentTheme = useTheme();
  const theme = useMemo(
    () =>
      isGenieOpen
        ? createTheme(parentTheme, {
            components: {
              MuiModal: { defaultProps: { disableEnforceFocus: true } },
            },
          })
        : parentTheme,
    [isGenieOpen, parentTheme]
  );

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={(globalTheme) => ({
          ":root": { [PAGE_RIGHT_INSET_VARIABLE]: inset },
          [PAGE_MODAL_SELECTOR]: {
            right: `var(${PAGE_RIGHT_INSET_VARIABLE}, 0px)`,
            transition: globalTheme.transitions.create("right", {
              easing: globalTheme.transitions.easing.easeIn,
              duration: globalTheme.transitions.duration.leavingScreen,
            }),
          },
          [RESIZING_PAGE_MODAL_SELECTOR]: { transition: "none" },
        })}
      />
      {children}
    </ThemeProvider>
  );
}
